import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  listenModelesHybrid,
  loadAppSettingsHybrid,
  ensureAppSettings,
  listenDossierRecordsHybrid,
  listenDocuments,
  deleteDossierRecord,
  saveUserSetting,
  getDossierRecordsPage,
  deleteDocumentMeta,
  deleteModele,
} from "../services/data";
import ShareLinksModal from "../components/ShareLinksModal.jsx";

export default function DossierPage({
  typeKey,
  user,
  onUse, // pour utiliser un template
  onEdit, // pour éditer un template
  onDelete, // pour supprimer un template
  onEditRecord, // [fire] pour éditer un document généré
  onDeleteRecord, // [fire] pour supprimer un document généré
}) {
  const ensuredOnceRef = useRef(false);
  const dossierKeyRef = useRef(typeKey);
  const recordIdsRef = useRef(new Set());
  const [dossiers, setDossiers] = useState([]);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const perPage = 5;
  const [templates, setTemplates] = useState([]);
  const [records, setRecords] = useState([]);
  const [olderRecords, setOlderRecords] = useState([]);
  const [recCursor, setRecCursor] = useState(null);
  const [isMoreRecsLoading, setIsMoreRecsLoading] = useState(false);
  const [qDocs, setQDocs] = useState("");
  const [fields, setFields] = useState([]);
  const [docs, setDocs] = useState([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);
  const loadMoreRecords = useCallback(async () => {
    if (!user?.uid || !typeKey || !recCursor) return;
    setIsMoreRecsLoading(true);
    const { items, cursor } = await getDossierRecordsPage(user.uid, {
      dossierKey: typeKey,
      pageSize: 30,
      afterDoc: recCursor,
    });
    setRecCursor(cursor);
    setOlderRecords((prev) => {
      const map = new Map(prev.map((x) => [x.id, x]));
      items.forEach((x) => {
        if (!map.has(x.id)) map.set(x.id, x);
      });
      return Array.from(map.values());
    });
    setIsMoreRecsLoading(false);
  }, [user?.uid, typeKey, recCursor]);
  const handleDeleteTemplate = useCallback(
    async (tpl) => {
      if (!user?.uid) return;
      const ok = confirm(
        `Supprimer le modèle "${tpl.nom}" ?\nCette action supprimera aussi le fichier et sa config.`
      );
      if (!ok) return;
      try {
        await deleteModele(user.uid, {
          id: tpl.id,
          storagePath: tpl.storagePath,
          configPath: tpl.configPath,
        });
        // Optionnel : retrait optimiste (le listener se mettra à jour de toute façon)
        // setTemplates(prev => prev.filter(x => x.id !== tpl.id));
      } catch (e) {
        console.error(e);
        alert("Échec de la suppression du modèle");
      }
    },
    [user?.uid]
  );
  const deleteTemplateUI = useCallback(
    async (tpl) => {
      if (!user?.uid) return;
      const ok = confirm(
        `Supprimer le modèle "${tpl.nom}" ?\nCette action supprimera aussi le fichier et la config.`
      );
      if (!ok) return;

      // 1) Retrait optimiste
      let rollbackIndex = -1;
      setTemplates((prev) => {
        const idx = prev.findIndex((x) => x.id === tpl.id);
        rollbackIndex = idx;
        if (idx === -1) return prev;
        const next = prev.slice();
        next.splice(idx, 1);
        return next;
      });

      try {
        // 2) Suppression réelle (parent ou fallback local)
        if (onDelete) {
          await onDelete(tpl);
        } else {
          await deleteModele(user.uid, {
            id: tpl.id,
            storagePath: tpl.storagePath,
            configPath: tpl.configPath,
          });
        }
        // 3) Rien à faire : le listener confirmera l'état
      } catch (e) {
        console.error(e);
        alert("Échec de la suppression du modèle.");
        // 4) Rollback si erreur
        if (rollbackIndex >= 0) {
          setTemplates((prev) => {
            const next = prev.slice();
            next.splice(rollbackIndex, 0, tpl);
            return next;
          });
        }
      }
    },
    [onDelete, user?.uid]
  );

  const onShareTemplateClick = useCallback(
    (t) => {
      if (!user?.uid) return alert("Connecte-toi pour partager.");
      const templateId = t?.id || t?.modeleId || t?.templateId;
      if (!templateId) return;

      // ✅ Ouvre seulement la modal, sans navigation
      setShareTarget(t);
      setShareOpen(true);
    },
    [user?.uid]
  );

  const openShare = useCallback((m) => {
    setShareTarget(m);
    setShareOpen(true);
  }, []);

  const closeShare = useCallback(() => {
    setShareOpen(false);
    setShareTarget(null);
  }, []);

  // [ok] Toujours re-filtrer côté UI au cas où le listener mélange des dossiers
  const scopedRecords = useMemo(() => {
    const k = typeKey;
    const base = [...(records || []), ...(olderRecords || [])];
    return base.filter((r) => {
      const dk = r?.dossierKey ?? r?.dossier ?? r?.values?.dossierKey ?? "";
      return dk === k;
    });
  }, [records, olderRecords, typeKey]);

  useEffect(() => {
    dossierKeyRef.current = typeKey || "";
  }, [typeKey]);

  useEffect(() => {
    recordIdsRef.current = new Set(
      scopedRecords.map((r) => r?.id).filter(Boolean)
    );
  }, [scopedRecords]);

  useEffect(() => {
    if (!user?.uid) {
      setDocs([]);
      return;
    }
    const unsub = listenDocuments(user.uid, (rows) => {
      const currentKey = (dossierKeyRef.current || "").trim();
      const recordIds = recordIdsRef.current;
      const filtered = rows.filter((d) => {
        const docKey = (d?.dossierKey || d?.dossier || "").trim();
        if (currentKey && docKey === currentKey) return true;
        const docRecordId = d?.dossierRecordId;
        if (docRecordId && recordIds.has(docRecordId)) return true;
        return false;
      });
      setDocs(filtered);
    });
    return () => {
      try {
        unsub && unsub();
      } catch {}
    };
  }, [user?.uid, typeKey]);

  const displayRecords = useMemo(() => {
    if (!docs.length) return scopedRecords;
    const recordMap = new Map(
      scopedRecords
        .map((rec) => [rec?.id, rec])
        .filter(([id]) => id != null && id !== "")
    );
    const matchedRecordIds = new Set();

    const merged = docs.map((doc) => {
      const recordId = doc?.dossierRecordId;
      if (recordId && recordMap.has(recordId)) {
        const existing = recordMap.get(recordId);
        matchedRecordIds.add(recordId);
        const fileName =
          existing.fileName || doc.title || doc.fileName || existing.id;
        const fileMeta =
          existing.file ||
          (doc.fileURL || doc.storagePath
            ? {
                name: fileName,
                storagePath: doc.storagePath,
                url: doc.fileURL,
                fileURL: doc.fileURL,
                size: doc.size,
              }
            : undefined);
        return {
          ...existing,
          documentMeta: doc,
          fileName,
          file: fileMeta ?? existing.file,
        };
      }

      return {
        id: doc.id,
        statut: doc.status || "Fini",
        values: {},
        fileName: doc.title || doc.fileName || doc.id,
        file:
          doc.fileURL || doc.storagePath
            ? {
                name: doc.title || doc.fileName || doc.id,
                storagePath: doc.storagePath,
                url: doc.fileURL,
                fileURL: doc.fileURL,
                size: doc.size,
              }
            : undefined,
        documentMeta: doc,
        createdAt: doc.createdAt,
        __orphanDocument: true,
      };
    });

    recordMap.forEach((record, id) => {
      if (!matchedRecordIds.has(id)) {
        merged.push(record);
      }
    });

    return merged;
  }, [docs, scopedRecords]);

  // Charger les documents générés (Hybride: cache -> live)
  useEffect(() => {
    if (!user?.uid || !typeKey) {
      setRecords([]);
      return;
    }
    const unsub = listenDossierRecordsHybrid(
      user.uid,
      { dossierKey: typeKey, pageSize: 30 }, // [lock] 30 derniers en live
      (rows) => setRecords(rows)
    );
    // seed du cursor pour Voir plus
    (async () => {
      const { cursor } = await getDossierRecordsPage(user.uid, {
        dossierKey: typeKey,
        pageSize: 30,
      });
      setRecCursor(cursor);
    })();
    return () => {
      try {
        unsub && unsub();
      } catch {}
    };
  }, [user?.uid, typeKey]);

  const currentType = dossiers.find((t) => t.key === typeKey);
  // Charger types + fields de dossiers (Hybride: cache -> live) + amorçage 1x si doc absent
  useEffect(() => {
    if (!user?.uid) return;
    const unsub = loadAppSettingsHybrid(user.uid, async (app) => {
      if (!app && !ensuredOnceRef.current) {
        ensuredOnceRef.current = true;
        try {
          app = await ensureAppSettings(user.uid);
        } catch (e) {
          console.warn("ensureAppSettings:", e?.message || e);
        }
      }
      const data = app?.dossiers || {};
      setDossiers(data.types || []);
      const allFields = data.fields || [];
      const filteredFields = allFields.filter(
        (f) => !f.applicableIds || f.applicableIds.includes(typeKey)
      );
      // [ok] respecte l'ordre défini dans l'éditeur
      const ordered = [...filteredFields].sort(
        (a, b) =>
          Number(a.order ?? 0) - Number(b.order ?? 0) ||
          (a.name || a.label || a.key || "").localeCompare(
            b.name || b.label || b.key || ""
          )
      );
      setFields(ordered);
    });
    return () => {
      try {
        unsub && unsub();
      } catch {}
    };
  }, [user?.uid, typeKey]);

  // Charger modèles (Hybride: cache -> live)
  useEffect(() => {
    if (!user?.uid) return;
    const unsub = listenModelesHybrid(user.uid, setTemplates);
    return () => {
      try {
        unsub && unsub();
      } catch {}
    };
  }, [user?.uid]);
  // [down] tri: 'desc' = plus récent d'abord (par défaut)
  const [sortOrder, setSortOrder] = useState("desc");
  useEffect(() => {
    console.log("[DossierPage] records reçus:", records.length, records[0]);
  }, [records]);

  // [down] util: extraire un timestamp robuste pour trier
  function recordToTs(r, idx) {
    const candidates = [
      r.createdAt,
      r.created_at,
      r.timestamp,
      r.ts,
      r._ts,
      r.date,
      r.meta?.createdAt,
      r.values?.createdAt,
      r.updatedAt,
    ];
    for (const c of candidates) {
      if (c == null || c === "") continue;
      // Firestore Timestamp
      if (typeof c === "object") {
        if (typeof c.toDate === "function") return c.toDate().getTime();
        if (typeof c.seconds === "number") {
          return c.seconds * 1000 + Math.floor((c.nanoseconds || 0) / 1e6);
        }
      }
      if (typeof c === "number") return c;
      const t = Date.parse(c);
      if (!Number.isNaN(t)) return t;
    }
    if (typeof r.id === "number") return r.id;
    const n = Number(r.id);
    if (!Number.isNaN(n)) return n;
    return idx; // fallback
  }

  // 1) Filtrage de base (recherche globale existante 'q')
  const filteredRecordsBase = useMemo(() => {
    if (!q) return displayRecords;
    const query = q.toLowerCase();
    return displayRecords.filter((r) => {
      const values = r.values || {};
      const matchFields = fields.some((f) =>
        (values[f.key] ?? "").toString().toLowerCase().includes(query)
      );
      const matchFile = (r.fileName ?? "").toLowerCase().includes(query);
      return matchFields || matchFile;
    });
  }, [displayRecords, q, fields]);

  // 2) Filtrage additionnel local (barre "Documents générés" -> qDocs)
  const filteredRecords = useMemo(() => {
    if (!qDocs) return filteredRecordsBase;
    const query = qDocs.toLowerCase();
    return filteredRecordsBase.filter((r) => {
      const v = r.values || {};
      // recherche sur: nom de fichier, statut, id, toutes les valeurs connues
      const haystack = [
        r.fileName || "",
        r.statut || "",
        r.id || "",
        ...fields.map((f) => v[f.key] ?? ""),
      ]
        .join(" ")
        .toString()
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [filteredRecordsBase, qDocs, fields]);
  // Ordonner après filtrage
  const orderedRecords = useMemo(() => {
    return filteredRecords
      .map((r, i) => ({ r, ts: recordToTs(r, i) }))
      .sort((a, b) => (sortOrder === "desc" ? b.ts - a.ts : a.ts - b.ts))
      .map((x) => x.r);
  }, [filteredRecords, sortOrder]);

  // Filtrer modèles du dossier courant + recherche
  const filteredTemplates = useMemo(() => {
    let result = templates.filter(
      (tpl) => tpl.dossier === typeKey || tpl.dossierKey === typeKey
    );
    if (q) {
      const query = q.toLowerCase();
      result = result.filter(
        (tpl) =>
          tpl.nom?.toLowerCase().includes(query) ||
          tpl.source?.toLowerCase().includes(query)
      );
    }
    return result;
  }, [templates, typeKey, q]);

  // Pagination sur documents générés (sur la liste triée)
  const totalPages = Math.max(1, Math.ceil(orderedRecords.length / perPage));
  const docsPage = orderedRecords.slice((page - 1) * perPage, page * perPage);

  // Quand totalPages diminue (filtre, tri...), on recadre la page courante
  useEffect(() => {
    setPage((p) => Math.min(p, totalPages));
  }, [totalPages]);

  return (
    <div className="dossier-page">
      {/* En-tête */}
      <div className="page-header">
        <h1 className="page-title">
          Dossier : {currentType?.label || typeKey}
        </h1>
        <div className="page-actions">
          <input
            type="text"
            className="filter-input"
            placeholder="Rechercher..."
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </div>

      {/* Templates disponibles */}
      <div className="templates-grid">
        {filteredTemplates.map((t) => (
          <div key={t.id} className="template-card">
            <div className="template-card-header">
              <h3 className="template-card-title">
                <i
                  className={`${
                    /^(fas|far|fal|fab)\sfa-/.test(t.icon || "")
                      ? t.icon
                      : `fas fa-${t.icon || "file"}`
                  }`}
                />{" "}
                {t.nom}
              </h3>
              <span className="template-type">{t.source || "Inconnu"}</span>
            </div>
            <div className="template-card-body">
              <div className="template-preview">
                <i
                  className={`${
                    /^(fas|far|fal|fab)\sfa-/.test(t.icon || "")
                      ? t.icon
                      : `fas fa-${t.icon || "file"}`
                  }`}
                />
              </div>
            </div>
            <div className="template-card-footer">
              <div className="template-info">
                <div className="template-info-item">
                  <i className="fas fa-calendar" /> Modifié: {t.modifie || "-"}
                </div>
                <div className="template-info-item">
                  <i className="fas fa-bolt" /> Utilisé {t.utilise || 0} fois
                </div>
              </div>
              <div className="template-actions">
                <div
                  className="action-btn"
                  title="Utiliser"
                  onClick={() => onUse && onUse(t)}
                >
                  <i className="fas fa-play" />
                </div>
                <div
                  className="action-btn"
                  title="Partager"
                  onClick={(e) => {
                    e.stopPropagation();
                    openShare(t);
                  }}
                >
                  <i className="fas fa-share-alt" />
                </div>
                <div
                  className="action-btn"
                  title="Modifier"
                  onClick={() => onEdit && onEdit(t)}
                >
                  <i className="fas fa-edit" />
                </div>
                <div
                  className="action-btn delete"
                  title="Supprimer"
                  onClick={() => deleteTemplateUI(t)}
                >
                  <i className="fas fa-trash" />
                </div>
              </div>
            </div>
          </div>
        ))}
        {filteredTemplates.length === 0 && (
          <p style={{ textAlign: "center", color: "#6b7280", width: "100%" }}>
            Aucun template trouvé pour ce dossier.
          </p>
        )}
      </div>

      {/* Documents générés */}
      <div className="table-container">
        <div className="table-header">
          <h3 className="table-title">Documents générés</h3>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            {/* [search] Recherche locale sur la table */}
            <input
              type="text"
              className="filter-input"
              placeholder="Rechercher dans les documents..."
              value={qDocs}
              onChange={(e) => {
                setQDocs(e.target.value);
                setPage(1);
              }}
              style={{ minWidth: 220 }}
            />
            <label className="filter-label">Tri</label>
            <select
              className="filter-input"
              value={sortOrder}
              onChange={(e) => {
                setSortOrder(e.target.value);
                setPage(1);
              }}
            >
              <option value="desc">Plus récent</option>
              <option value="asc">Plus ancien</option>
            </select>
            {qDocs && (
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => {
                  setQDocs("");
                  setPage(1);
                }}
              >
                Effacer
              </button>
            )}
          </div>
        </div>
        <table>
          <thead>
            <tr>
              {fields.map((f) => (
                <th key={f.key}>{f.label || f.name || f.key}</th>
              ))}
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {docsPage.map((r, idx) => (
              <tr key={r.id || idx}>
                {fields.map((f) => (
                  <td key={`${f.key}-${idx}`}>{r?.values?.[f.key] ?? ""}</td>
                ))}
                <td>
                  <span
                    className={`badge ${
                      r.statut === "Fini" ? "success" : "warning"
                    }`}
                  >
                    {r.statut}
                  </span>
                </td>
                <td className="table-actions">
                  {/* [fire] Ici on appelle onEditRecord et pas onEdit */}
                  <button
                    onClick={() => {
                      if (r.__orphanDocument) return;
                      onEditRecord && onEditRecord(r);
                    }}
                    className={`icon-btn${
                      r.__orphanDocument ? " disabled" : ""
                    }`}
                    disabled={r.__orphanDocument}
                    title={
                      r.__orphanDocument
                        ? "Edition indisponible pour ce document"
                        : undefined
                    }
                  >
                    <i className="fas fa-edit"></i>
                  </button>
                  <button
                    onClick={async () => {
                      if (r.__orphanDocument) return;
                      if (!confirm("Supprimer ce document généré ?")) return;

                      const docId = r.documentMeta?.id || null;

                      // 1) supprimer le brouillon / record
                      await deleteDossierRecord(user.uid, {
                        id: r.id,
                        storagePath: r.file?.storagePath,
                      });

                      // 2) s'il y a un meta dans users/{uid}/documents -> on le supprime aussi
                      if (docId) {
                        await deleteDocumentMeta(user.uid, docId);
                      }

                      // 3) MAJ locale immédiate (pas besoin d'actualiser)
                      setRecords((prev) => prev.filter((x) => x.id !== r.id));
                      setOlderRecords((prev) =>
                        prev.filter((x) => x.id !== r.id)
                      );
                      setDocs((prev) =>
                        prev.filter(
                          (d) => d.id !== docId && d.dossierRecordId !== r.id
                        )
                      );
                    }}
                    className={`icon-btn danger${
                      r.__orphanDocument ? " disabled" : ""
                    }`}
                    disabled={r.__orphanDocument}
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="table-footer">
          <span>
            Page {page} sur {totalPages}
          </span>
          <div className="pagination">
            <div
              className={`pagination-btn ${page === 1 ? "disabled" : ""}`}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <i className="fas fa-chevron-left"></i>
            </div>
            <div
              className={`pagination-btn ${
                page === totalPages ? "disabled" : ""
              }`}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              <i className="fas fa-chevron-right"></i>
            </div>
          </div>
        </div>
      </div>
      {recCursor && (
        <div style={{ textAlign: "center", margin: "12px 0" }}>
          <button
            className="btn btn-outline"
            disabled={isMoreRecsLoading}
            onClick={loadMoreRecords}
          >
            {isMoreRecsLoading ? "Chargement..." : "Voir plus de documents"}
          </button>
        </div>
      )}

      <ShareLinksModal
        open={shareOpen}
        onClose={closeShare}
        user={user}
        template={shareTarget}
      />
    </div>
  );
}
